import type { CatServiceAPIImage } from 'src/services/cat-service-api/types/CatServiceAPIImage';
import type { CatServiceAPIVideo } from 'src/services/cat-service-api/types/CatServiceAPIVideo';
import type { ImageMediaItem, MediaItem, VideoMediaItem } from '../types';

/** Width (px) for the full-size poster shown in the carousel's main slot and the modal. Matches the `original` image preset. */
export const VIDEO_POSTER_WIDTH = 1024;

/** Width (px) for the video poster in the thumbnail bar. The `mini` (90px) slot at 2x for retina screens. */
export const VIDEO_THUMBNAIL_WIDTH = 180;

/**
 * Poster from the video's first frame (Mux thumbnail at time=0), resized to the slot that renders it.
 * Mux scales the longest edge to `width` (fit_mode defaults to preserve), so we only request the pixels we display.
 */
export const videoPosterUrl = (playbackId: string, width: number) =>
  `https://image.mux.com/${playbackId}/thumbnail.webp?time=0&width=${width}`;

const DEFAULT_VIDEO_TITLE = 'Product video';
const DEFAULT_ASPECT_RATIO = '16:9';

const toVideoMediaItem = (video: CatServiceAPIVideo): VideoMediaItem => ({
  aspectRatio: video.aspectRatio ?? DEFAULT_ASPECT_RATIO,
  playbackId: video.playbackId,
  posterUrl: videoPosterUrl(video.playbackId, VIDEO_POSTER_WIDTH),
  title: video.alt ?? DEFAULT_VIDEO_TITLE,
  type: 'video',
});

export const buildProductMedia = (
  images: CatServiceAPIImage[],
  video?: CatServiceAPIVideo | null,
): MediaItem[] => {
  const imageItems: ImageMediaItem[] = images.map((image) => ({ ...image, type: 'image' }));

  if (!video) {
    return imageItems;
  }

  // video.position is 1-based within the combined media list (images + video)
  const insertAt = Math.min(Math.max(video.position - 1, 0), imageItems.length);

  return [...imageItems.slice(0, insertAt), toVideoMediaItem(video), ...imageItems.slice(insertAt)];
};

/** Stable React key for a media item, used by every carousel/thumbnail/modal consumer. */
export const mediaKey = (item: MediaItem): string =>
  item.type === 'video' ? item.playbackId : item.href;
